import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subject,
  Subscription,
  debounceTime,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { CreateUserFormComponent } from '../create-user/create-user-form.component';
import { User, UsersServiceInterface, UsersServiceInterfaceToken } from '../../users-service-interface';

export enum UserAction {
  ADD = 'add',
  CREATE = 'create',
}
export interface AddUserResponse {
  action: UserAction;
  users: User[];
}
export interface SendEmailDialogData {
  showCreateTab: boolean;
  partnerId: string;
  businessId: string;
  existingUsers: User[];
}

@Component({
  styleUrls: ['add-user-dialog.component.scss'],
  templateUrl: './add-user-dialog.component.html',
  standalone: false,
})
export class AddUserDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  subscriptions: Subscription[] = [];
  accountGroupId = '';
  partnerId = '';
  showCreateTab = false;

  @ViewChild('createForm')
  createFormComponent!: CreateUserFormComponent;

  term$ = new Subject<string>();
  hasMoreResults$!: Observable<boolean>;
  users$!: Observable<User[]>;
  loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  loading$ = this.loading$$.asObservable();

  existingUsers: User[] = [];
  selectedUsers: User[] = [];
  addedUsers: User[] = [];

  private isLoading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoading$$.asObservable();

  private tab$$: ReplaySubject<UserAction> = new ReplaySubject<UserAction>(1);
  tab$ = this.tab$$.asObservable();

  private _destroy$$ = new Subject<void>();
  UserAction = UserAction;

  constructor(
    public dialogRef: MatDialogRef<AddUserDialogComponent, AddUserResponse | string | User[]>,
    @Inject(MAT_DIALOG_DATA) public data: SendEmailDialogData,
    @Inject(UsersServiceInterfaceToken) private userService: UsersServiceInterface,
  ) {
    this.showCreateTab = data?.showCreateTab;
    this.partnerId = data?.partnerId;
    this.accountGroupId = data?.businessId;
    this.existingUsers = data?.existingUsers;
  }

  ngOnInit(): void {
    this.tab$$.next(this.showCreateTab ? UserAction.CREATE : UserAction.ADD);
    //Set up initial search.
    this.userService.search('');
    this.subscriptions.push(
      this.term$.pipe(debounceTime(200)).subscribe((x) => {
        this.loading$$.next(true);
        this.userService.search(x);
      }),
    );

    this.hasMoreResults$ = this.userService.hasMoreResults$;
    this.users$ = this.userService.users$.pipe(tap(() => this.loading$$.next(false)));
  }

  ngAfterViewInit(): void {
    if (this.showCreateTab) {
      this.createFormComponent
        .isCreatingUser()
        .pipe(takeUntil(this._destroy$$))
        .subscribe((isLoading) => this.isLoading$$.next(isLoading));
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this._destroy$$.next();
    this._destroy$$.complete();
  }

  addSelectedUsers(): void {
    this.isLoading$$.next(true);
    const usersToAdd: User[] = [];
    for (const user of this.selectedUsers) {
      if (!this.doesUserExist(user)) {
        usersToAdd.push(user);
      }
    }
    if (usersToAdd.length > 0) {
      this.addedUsers.push(...usersToAdd);
      this.userService
        .addUsersToAccount(
          this.partnerId,
          this.accountGroupId,
          usersToAdd.map((x) => x.userId),
        )
        .subscribe({
          next: () => this.dialogRef.close({ action: UserAction.ADD, users: this.addedUsers }),
          error: () => this.dialogRef.close(),
          complete: () => this.isLoading$$.next(false),
        });
    }
  }

  addUserToSelected(event: MatCheckboxChange, user: User): void {
    if (event.checked) {
      this.selectedUsers.push(user);
    } else {
      const index = this.selectedUsers.indexOf(user, 0);
      if (index > -1) {
        this.selectedUsers.splice(index, 1);
      }
    }
  }

  doesUserExist(user: User): boolean {
    if (!this.existingUsers) {
      return false;
    }
    return (
      [...this.addedUsers, ...this.existingUsers].filter((existingUser) => existingUser.email === user.email).length > 0
    );
  }

  userCreated(user: User): void {
    this.dialogRef.close({ action: UserAction.CREATE, users: [user] });
  }

  tabChanged(event: MatTabChangeEvent): void {
    this.tab$$.next(event.index === 0 ? UserAction.CREATE : UserAction.ADD);
  }

  createUser(): void {
    this.createFormComponent.createUser();
  }

  onSubmit(): void {
    this.tab$.pipe(take(1)).subscribe((tab) => (tab === UserAction.ADD ? this.addSelectedUsers() : this.createUser()));
  }

  loadMore(): void {
    this.userService.loadMore();
  }
}
